import { FC, useCallback, useId, useMemo, useState } from 'react';
import clsx from 'clsx';

import { WalletWidgetContainerProps } from './WalletWidgetContainer.types';
import { StyledWalletWidgetContainer } from './WalletWidgetContainer.styles';
import { WalletWidgetContainerContext, buildContainerCountBadgeText } from './WalletWidgetContainerContext';
import { WalletWidgetContainerHeader } from './components/WalletWidgetContainerHeader';
import { WalletWidgetContainerBody } from './components/WalletWidgetContainerBody';

export const WalletWidgetContainer: FC<WalletWidgetContainerProps> = ({
  availablePluralLabel,
  availableSingularLabel,
  badgeIcon,
  children,
  componentId,
  cqItems,
  customClass,
  defaultBadgeLabel,
  headerIcon,
  headerLabel,
  hideComponent,
  isAuthorMode,
  itemPath,
  pagePath,
  totalNumberOfCards,
  zeroAvailableLabel,
}) => {
  const autoId = useId();
  const id = componentId || autoId;

  const [publishedTexts, setPublishedTexts] = useState<Record<string, string>>({});

  const publishText = useCallback((publisherId: string, text: string | undefined) => {
    setPublishedTexts(prev => {
      if (text === undefined) {
        if (!(publisherId in prev)) return prev;
        const next = { ...prev };
        delete next[publisherId];
        return next;
      }
      if (prev[publisherId] === text) return prev;
      return { ...prev, [publisherId]: text };
    });
  }, []);

  const publishedText = useMemo(() => {
    const values = Object.values(publishedTexts);
    return values.length ? values[values.length - 1] : undefined;
  }, [publishedTexts]);

  const resolvedPublishedText = useMemo(() => {
    if (!publishedText) return publishedText;

    const trimmed = publishedText.trim();
    if (!/^\d+$/.test(trimmed)) return publishedText;

    const count = Number(trimmed);
    const formatted = buildContainerCountBadgeText(count, {
      defaultLabel: defaultBadgeLabel,
      availableSingularLabel,
      availablePluralLabel,
      zeroAvailableLabel,
    });

    return formatted || publishedText;
  }, [publishedText, defaultBadgeLabel, availableSingularLabel, availablePluralLabel, zeroAvailableLabel]);

  const contextValue = useMemo(() => ({ publishedText, publishText }), [publishedText, publishText]);

  if (hideComponent) return null;

  return (
    <WalletWidgetContainerContext.Provider value={contextValue}>
      <StyledWalletWidgetContainer
        data-testid="account-WalletWidgetContainer"
        data-component-name="o-account-WalletWidgetContainer"
        className={clsx('wallet-widget-container', customClass)}
        id={id}
      >
        {headerLabel && (
          <WalletWidgetContainerHeader
            badgeIcon={badgeIcon}
            headerIcon={headerIcon}
            headerLabel={headerLabel}
            publishedText={resolvedPublishedText}
          />
        )}
        <WalletWidgetContainerBody
          cqItems={cqItems}
          isAuthorMode={isAuthorMode}
          itemPath={itemPath}
          pagePath={pagePath}
          totalNumberOfCards={totalNumberOfCards}
        >
          {children}
        </WalletWidgetContainerBody>
      </StyledWalletWidgetContainer>
    </WalletWidgetContainerContext.Provider>
  );
};
